import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { apiClient } from '../api/client';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Separator } from '../components/ui/separator';
import {
  ArrowLeft,
  Send,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  User,
  Building2,
  Info,
  Loader2,
  Clock,
  FileText,
  Plus,
  X
} from 'lucide-react';
import { Typewriter } from '../components/Typewriter';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import VoiceInput from '../components/VoiceInput';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

interface Message {
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  evaluator_analysis?: any;
  animate?: boolean;
}

interface PatientChart {
  patient_description?: string;
  admission_reason?: string;
  medical_surgical_history?: string;
}

const CLASSIFICATION_TYPES = [
  "Misinformation or Misconceptions",
  "Emotional Discomfort or Fear",
  "Communication Barriers",
  "Financial or Insurance-Related Concern"
];

const ConversationPage: React.FC = () => {
  const { patientId, sessionId: urlSessionId } = useParams<{ patientId?: string; sessionId?: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const groupCMode = searchParams.get('groupc') === '1';
  const selectedTask = (searchParams.get('task') || '').toLowerCase();

  const [sessionId, setSessionId] = useState<string | null>(null);
  const [patientName, setPatientName] = useState<string>('');
  const [patientChart, setPatientChart] = useState<PatientChart | null>(null);
  const [chartDrawerOpen, setChartDrawerOpen] = useState(() => window.innerWidth >= 1024);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(true);
  const [isInterventionSession, setIsInterventionSession] = useState<boolean>(selectedTask === 'intervention');
  const [allHiddenConcernsRevealed, setAllHiddenConcernsRevealed] = useState(false);

  // Dynamic findings state
  const [addedFindings, setAddedFindings] = useState<{category: string, description: string}[]>([]);
  const [currentCategory, setCurrentCategory] = useState(CLASSIFICATION_TYPES[0]);
  const [currentDescription, setCurrentDescription] = useState('');
  
  const [submitting, setSubmitting] = useState(false);
  const [timeLeft, setTimeLeft] = useState(600);

  const messagesEndRef = useRef<HTMLDivElement>(null);

  const parseEvaluatorAnalysis = (value: any): Record<string, any> | null => {
    if (!value) return null;
    if (typeof value === 'object') return value;
    if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value);
        if (parsed && typeof parsed === 'object') return parsed;
      } catch {
        return null;
      }
    }
    return null;
  };

  const hasAllRevealedFromHistory = (history: any[]): boolean => {
    if (!Array.isArray(history)) return false;
    return history.some((msg: any) => {
      if (msg?.role !== 'assistant') return false;
      const evaluator = parseEvaluatorAnalysis(msg?.evaluator_analysis);
      const decision = String(evaluator?.reveal_decision || '').toLowerCase().trim();
      return decision === 'all_revealed';
    });
  };

  const refreshAllRevealedStatus = async (sid: string) => {
    try {
      const historyResponse = await apiClient.get(`/api/conversation/history/${sid}`);
      const history = Array.isArray(historyResponse.data?.conversation_history)
        ? historyResponse.data.conversation_history
        : [];
      setAllHiddenConcernsRevealed(hasAllRevealedFromHistory(history));
    } catch (error) {
      console.error('Failed to refresh reveal status:', error);
    }
  };

  const handleAddFinding = () => {
    if (!currentDescription.trim()) return;
    setAddedFindings(prev => [...prev, { category: currentCategory, description: currentDescription }]);
    setCurrentDescription('');
  };

  const handleRemoveFinding = (index: number) => {
    setAddedFindings(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmitFindings = async () => {
    if (!sessionId) return;
    
    let finalFindings = [...addedFindings];
    if (currentDescription.trim()) {
        finalFindings.push({ category: currentCategory, description: currentDescription });
    }

    if (finalFindings.length === 0) {
      alert("Please enter at least one finding before submitting.");
      return;
    }

    setSubmitting(true);
    try {
      await apiClient.post('/api/conversation/submit', {
        session_id: sessionId,
        findings: finalFindings
      });
      navigate(`/evaluation/${sessionId}`);
    } catch (error: any) {
      console.error('Failed to submit findings:', error);
      alert(error.response?.data?.detail || 'Failed to submit findings. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (urlSessionId) {
      resumeConversation(urlSessionId);
    } else if (patientId) {
      startConversation();
    }
  }, [patientId, urlSessionId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (timeLeft <= 0) return;
    if (timeLeft === 120) {
        alert("Reminder: 2 minutes remaining in this session.");
    }
    const timer = setInterval(() => {
      setTimeLeft((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [timeLeft]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const startConversation = async () => {
    try {
      const enableIntervention = selectedTask === 'intervention';
      setIsInterventionSession(enableIntervention);
      setAllHiddenConcernsRevealed(false);

      const patientResponse = await apiClient.get(`/api/patients/${patientId}`);
      setPatientName(patientResponse.data.name);
      setPatientChart({
        patient_description: patientResponse.data.patient_description,
        admission_reason: patientResponse.data.admission_reason,
        medical_surgical_history: patientResponse.data.medical_surgical_history
      });

      const sessionResponse = await apiClient.post(`/api/conversation/start`, {
        patient_id: patientId,
        model_name: 'gpt-4o',
        enable_intervention: enableIntervention
      });
      setSessionId(sessionResponse.data.session_id);
    } catch (error) {
      console.error('Failed to start conversation:', error);
      alert('Failed to start conversation. Please try again.');
      if (groupCMode) {
        navigate('/group-c-selection');
      } else {
        navigate('/patient-chart/patient_0');
      }
    } finally {
      setInitializing(false);
    }
  };

  const resumeConversation = async (existingSessionId: string) => {
    try {
      const sessionResponse = await apiClient.get(`/api/sessions/${existingSessionId}`);
      const sessionData = sessionResponse.data;
      setSessionId(existingSessionId);
      setPatientName(sessionData.patient_name);
      setIsInterventionSession(Boolean(sessionData.is_intervention));

      const history: Message[] = sessionData.conversation_history.map((msg: any) => ({
        role: msg.role,
        content: msg.content,
        timestamp: msg.timestamp,
        evaluator_analysis: msg.evaluator_analysis
      }));
      setMessages(history);
      setAllHiddenConcernsRevealed(hasAllRevealedFromHistory(sessionData.conversation_history || []));

      const patientResponse = await apiClient.get(`/api/patients/${sessionData.patient_id}`);
      setPatientChart({
        patient_description: patientResponse.data.patient_description,
        admission_reason: patientResponse.data.admission_reason,
        medical_surgical_history: patientResponse.data.medical_surgical_history
      });
    } catch (error) {
      console.error('Failed to resume conversation:', error);
      alert('Failed to resume conversation. Please try again.');
      if (groupCMode) {
        navigate('/group-c-selection');
      } else {
        navigate('/patient-chart/patient_0');
      }
    } finally {
      setInitializing(false);
    }
  };

  const handleSendMessage = async () => {
    const text = inputMessage.trim();
    if (!text || !sessionId) return;

    const userMessage: Message = {
      role: 'user',
      content: text,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setLoading(true);

    try {
      const assistantMessage: Message = {
        role: 'assistant',
        content: '',
        timestamp: new Date().toISOString(),
        animate: false
      };
      setMessages(prev => [...prev, assistantMessage]);

      const token = localStorage.getItem('token');
      const response = await fetch(`${apiClient.defaults.baseURL}/api/conversation/message/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          session_id: sessionId,
          message: text,
          use_tts: false,
          from_audio: false
        })
      });

      if (!response.body) throw new Error("No response body");
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let done = false;
      let fullText = "";
      setLoading(false);

      while (!done) {
        const { value, done: doneReading } = await reader.read();
        done = doneReading;
        if (value) {
            const chunkValue = decoder.decode(value, { stream: !done });
            fullText += chunkValue;
            setMessages(prev => {
              const newMessages = [...prev];
              const lastMsg = newMessages[newMessages.length - 1];
              if (lastMsg.role === 'assistant') { lastMsg.content = fullText; }
              return newMessages;
            });
        }
      }
      await refreshAllRevealedStatus(sessionId);
    } catch (error) {
      console.error('Failed to send message:', error);
      alert('Failed to send message. Please try again.');
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const toggleChartDrawer = () => {
    setChartDrawerOpen(!chartDrawerOpen);
  };

  const userMessageCount = messages.filter(m => m.role === 'user').length;
  const minConfirmationRounds = 5;
  const canSubmit = userMessageCount >= minConfirmationRounds;
  const showAllRevealedNudge = !isInterventionSession && allHiddenConcernsRevealed;

  if (initializing) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-primary text-primary-foreground shadow-md">
        <div className="w-full px-4 py-4 flex items-center justify-between">
          <div className="flex items-start gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() =>
                navigate(
                  groupCMode
                    ? `/patient-chart/${patientId}?task=${selectedTask || 'confirmation'}&groupc=1`
                    : `/patient-chart/${patientId}`
                )
              }
              className="text-primary-foreground hover:bg-primary-foreground/10"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-xl font-bold">Encounter with {patientName}</h1>
              <p className="text-xs text-primary-foreground/80 mt-1">
                Confirmation task: ask questions to uncover as many different hidden concerns as possible, then submit your findings. Prioritize broad probing across concerns rather than addressing or going too deep on just one.
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2 bg-primary-foreground/10 px-3 py-1 rounded-md">
            <Clock className="h-4 w-4" />
            <span className={`font-mono font-medium ${timeLeft < 60 ? 'text-red-300' : ''}`}>
              {formatTime(timeLeft)}
            </span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        <div className={`flex-1 flex flex-col m-2 transition-all duration-300 ${chartDrawerOpen ? 'lg:mr-[400px]' : 'mr-2'}`}>
          <Card className="flex-1 flex flex-col overflow-hidden shadow-lg">
            <div className="flex-1 overflow-y-auto p-6">
              {showAllRevealedNudge && (
                <div className="mb-4 rounded-md border border-amber-300 bg-amber-50 p-3 text-amber-900">
                  <div className="flex items-start gap-2">
                    <Info className="h-4 w-4 mt-0.5" />
                    <p className="text-sm">
                      All hidden concerns appear to be revealed. You should be ready to summarize key concerns and submit your findings.
                    </p>
                  </div>
                </div>
              )}
              {messages.length === 0 ? (
                <div className="text-center text-muted-foreground mt-8">
                  Start the conversation by asking a question.
                  <p className="text-sm mt-2">You have 10 minutes to uncover the patient's hidden concerns.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {messages.map((message, index) => (
                    <div
                      key={index}
                      className={`flex flex-col rounded-lg p-4 max-w-[85%] ${
                        message.role === 'user' ? 'bg-blue-100 self-end ml-auto' : 'bg-gray-100 self-start mr-auto'
                      }`}
                    >
                      <span className="text-xs font-semibold text-muted-foreground mb-1">
                        {message.role === 'user' ? 'You' : patientName}
                      </span>
                      <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>
              )}
            </div>

            <Separator />
            
            <div className="p-4 bg-gray-50 border-t">
               <div className="mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <Label className="font-semibold text-gray-700">Findings Submission</Label>
                    <span className="text-xs text-muted-foreground">{userMessageCount}/{minConfirmationRounds} rounds</span>
                  </div>
                  
                  {/* Added Findings List */}
                  {addedFindings.length > 0 && (
                    <div className="mb-3 space-y-2 max-h-[120px] overflow-y-auto">
                        {addedFindings.map((f, idx) => (
                            <div key={idx} className="flex items-start justify-between bg-blue-50 border border-blue-100 rounded p-2 group">
                                <div className="text-xs">
                                    <span className="font-bold text-blue-800">{f.category}:</span>
                                    <p className="text-gray-700 inline ml-1">{f.description}</p>
                                </div>
                                <button onClick={() => handleRemoveFinding(idx)} className="text-gray-400 hover:text-red-500 ml-2">
                                    <X className="h-3 w-3" />
                                </button>
                            </div>
                        ))}
                    </div>
                  )}

                  {/* Input Row */}
                  <div className="border rounded-md p-3 bg-white space-y-3 shadow-sm">
                    <div className="flex flex-col sm:flex-row gap-3">
                        <div className="w-full sm:w-[250px]">
                            <Select value={currentCategory} onValueChange={setCurrentCategory} disabled={!canSubmit}>
                                <SelectTrigger className="h-9 text-xs">
                                    <SelectValue placeholder="Select classification..." />
                                </SelectTrigger>
                                <SelectContent>
                                    {CLASSIFICATION_TYPES.map(type => (
                                        <SelectItem key={type} value={type} className="text-xs">{type}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="flex-1 flex gap-2">
                            <Textarea
                                placeholder="Enter fine-grained concern... (use ; for multiples)"
                                value={currentDescription}
                                onChange={(e) => setCurrentDescription(e.target.value)}
                                className="min-h-[36px] h-9 py-2 text-xs resize-none"
                                disabled={!canSubmit}
                            />
                            <Button 
                                variant="outline" 
                                size="sm" 
                                onClick={handleAddFinding} 
                                disabled={!canSubmit || !currentDescription.trim()}
                                className="h-9 px-2"
                            >
                                <Plus className="h-4 w-4" />
                            </Button>
                        </div>
                    </div>
                  </div>
               </div>
               
               <p className="text-xs text-muted-foreground mb-3">
                 Each patient in this study has multiple hidden concerns. Please submit all hidden concerns you identify, not just one.
               </p>

                <div className="flex justify-between items-center">
                     <div className="flex-1 mr-4">
                        <div className="flex gap-2">
                            <VoiceInput 
                                onTranscription={(text) => setInputMessage(prev => prev ? `${prev} ${text}` : text)}
                                disabled={loading}
                            />
                            <Input
                            className="flex-1"
                            placeholder="Type your question here..."
                            value={inputMessage}
                            onChange={(e) => setInputMessage(e.target.value)}
                            onKeyPress={handleKeyPress}
                            disabled={loading}
                            />
                            <Button onClick={() => handleSendMessage()} disabled={!inputMessage.trim() || loading}>
                                <Send className="h-4 w-4 mr-2" />Send
                            </Button>
                        </div>
                    </div>
                    <Button 
                      onClick={handleSubmitFindings} 
                      disabled={!canSubmit || submitting} 
                      className={canSubmit ? "bg-green-600 hover:bg-green-700" : ""}
                    >
                        Submit Findings
                    </Button>
               </div>
            </div>
          </Card>
        </div>

        {/* Sidebar */}
        <div className={`fixed right-0 top-16 h-[calc(100vh-4rem)] w-full lg:w-[400px] bg-white shadow-2xl transition-all duration-300 transform overflow-y-auto ${chartDrawerOpen ? 'translate-x-0' : 'translate-x-full'} z-10`}>
          <Button variant="default" size="sm" onClick={toggleChartDrawer} className="absolute -left-10 top-1/2 transform -translate-y-1/2 rounded-r-none">
            {chartDrawerOpen ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </Button>

          <div className="p-4 space-y-4">
            <h3 className="font-semibold text-lg">Patient Chart</h3>
            <Separator />

            {patientChart?.patient_description && (
                <Card className="border shadow-none">
                    <CardHeader className="p-3 pb-0 flex flex-row items-center gap-2">
                        <User className="h-4 w-4 text-blue-600" />
                        <CardTitle className="text-sm">Patient Description</CardTitle>
                    </CardHeader>
                    <CardContent className="p-3">
                        <p className="text-xs whitespace-pre-wrap">{patientChart.patient_description}</p>
                    </CardContent>
                </Card>
            )}

            {patientChart?.admission_reason && (
                <Card className="border shadow-none">
                    <CardHeader className="p-3 pb-0 flex flex-row items-center gap-2">
                        <Building2 className="h-4 w-4 text-red-600" />
                        <CardTitle className="text-sm">Encounter Reason</CardTitle>
                    </CardHeader>
                    <CardContent className="p-3">
                        <p className="text-xs whitespace-pre-wrap">{patientChart.admission_reason}</p>
                    </CardContent>
                </Card>
            )}

            {patientChart?.medical_surgical_history && (
                <Card className="border shadow-none">
                    <CardHeader className="p-3 pb-0 flex flex-row items-center gap-2">
                        <FileText className="h-4 w-4 text-purple-600" />
                        <CardTitle className="text-sm">Medical & Surgical History</CardTitle>
                    </CardHeader>
                    <CardContent className="p-3">
                        <p className="text-xs whitespace-pre-wrap">{patientChart.medical_surgical_history}</p>
                    </CardContent>
                </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConversationPage;


